/* global BigInt */
// Admin: ledger of every logged buy (Postgres `purchases` table joined with
// Clerk emails server-side). Read-only view; filter by wallet, tx, or email.

import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { formatUnits } from 'viem';
import { apiClient } from '../services/apiService';
import { addressUrl, txUrl } from '../config/chains';
import './AdminTransactions.css';

const TOKEN_SYMBOL = process.env.REACT_APP_TOKEN_SYMBOL || 'FBIZ';

function shortAddr(a) {
  if (!a) return '—';
  return `${a.slice(0, 6)}…${a.slice(-4)}`;
}

function formatRelative(iso) {
  const then = new Date(iso).getTime();
  if (!then) return '—';
  const diff = Date.now() - then;
  const s = Math.max(1, Math.floor(diff / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

function formatAbsolute(iso) {
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

// `wei` is a base-units string from the API (DECIMAL 36,0). 18 decimals matches
// both USDT-on-BSC and the FBIZ token. Trim to `fractionDigits` for display.
function formatBase18(wei, fractionDigits = 2) {
  if (wei == null) return '—';
  try {
    const human = formatUnits(BigInt(wei), 18);
    const [int, frac = ''] = human.split('.');
    const intGrouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    if (fractionDigits === 0) return intGrouped;
    const fracTrim = (frac + '0'.repeat(fractionDigits)).slice(0, fractionDigits);
    return `${intGrouped}.${fracTrim}`;
  } catch {
    return String(wei);
  }
}

function CopyButton({ value, label }) {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  return (
    <button
      type="button"
      className="adx-copy"
      aria-label={`Copy ${label}`}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        navigator.clipboard?.writeText(value);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 700);
      }}
    >
      {copied ? '✓' : '⧉'}
    </button>
  );
}

function useDebounced(value, delay = 250) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

function StatBlock({ eyebrow, value, hint }) {
  return (
    <div className="adx-stat">
      <span className="adx-stat__eyebrow">{eyebrow}</span>
      <span className="adx-stat__value">{value}</span>
      {hint ? <span className="adx-stat__hint">{hint}</span> : null}
    </div>
  );
}

function SkeletonRow({ idx }) {
  return (
    <tr className="adx-row adx-row--skeleton" aria-hidden>
      <td className="adx-col-num">{String(idx + 1).padStart(3, '0')}</td>
      <td><span className="adx-shimmer" style={{ width: '5rem' }} /></td>
      <td>
        <span className="adx-shimmer" style={{ width: '12rem' }} />
        <span className="adx-shimmer" style={{ width: '9rem', marginTop: '0.3rem' }} />
      </td>
      <td><span className="adx-shimmer" style={{ width: '5rem', marginLeft: 'auto' }} /></td>
      <td><span className="adx-shimmer" style={{ width: '7rem', marginLeft: 'auto' }} /></td>
      <td><span className="adx-shimmer" style={{ width: '2rem', marginLeft: 'auto' }} /></td>
    </tr>
  );
}

export default function AdminTransactions() {
  const [params, setParams] = useSearchParams();
  const page = Math.max(1, parseInt(params.get('page'), 10) || 1);
  const limit = 25;
  const initialQ = params.get('q') || '';

  const [query, setQuery] = useState(initialQ);
  const debouncedQ = useDebounced(query.trim(), 300);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);

  // Sync the URL whenever the debounced query or page changes. Resets to page 1
  // on a new search so the admin doesn't land on an empty page N of a filtered set.
  useEffect(() => {
    const next = new URLSearchParams(params);
    if (debouncedQ) next.set('q', debouncedQ); else next.delete('q');
    const queryChanged = (params.get('q') || '') !== debouncedQ;
    if (queryChanged) next.set('page', '1');
    if (next.toString() !== params.toString()) {
      setParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQ]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiClient
      .get('/purchases', { params: { page, limit, q: debouncedQ || undefined } })
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch((err) => {
        if (cancelled) return;
        const msg = err?.response?.data?.message || err?.message || 'Failed to load purchases';
        setError(msg);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [page, limit, debouncedQ, refreshTick]);

  const rows = data?.rows || [];
  const total = data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const usdtTotal = data?.totals?.usdtIn;
  const tokensTotal = data?.totals?.tokensOut;
  const buyersTotal = data?.totals?.buyers ?? 0;

  const rangeStart = total === 0 ? 0 : (page - 1) * limit + 1;
  const rangeEnd = Math.min(total, page * limit);

  const goto = (p) => {
    const next = new URLSearchParams(params);
    next.set('page', String(Math.min(Math.max(1, p), totalPages)));
    setParams(next, { replace: true });
  };

  const headerHint = useMemo(() => {
    if (!debouncedQ) return 'All on-chain buys logged after server verification.';
    return `Filtered by “${debouncedQ}”.`;
  }, [debouncedQ]);

  return (
    <div className="adx-page">
      <div className="adx-shell">
        <header className="adx-header">
          <div className="adx-header__lede">
            <span className="adx-eyebrow">
              <span className="adx-dot" aria-hidden /> Presale · Live ledger
            </span>
            <h1 className="adx-title">Transaction<br />history</h1>
            <p className="adx-sub">{headerHint}</p>
            <div className="adx-backlink">
              <Link to="/admin">‹ admin home</Link>
              <Link to="/admin/presale">presale settings ›</Link>
            </div>
          </div>

          <div className="adx-stats">
            <StatBlock
              eyebrow="USDT raised"
              value={usdtTotal != null ? formatBase18(usdtTotal, 2) : '—'}
              hint={debouncedQ ? 'in filtered set' : 'all-time'}
            />
            <StatBlock
              eyebrow={`${TOKEN_SYMBOL} sold`}
              value={tokensTotal != null ? formatBase18(tokensTotal, 0) : '—'}
              hint={debouncedQ ? 'in filtered set' : 'all-time'}
            />
            <StatBlock
              eyebrow="Unique buyers"
              value={buyersTotal != null ? buyersTotal.toLocaleString() : '—'}
              hint={`${total.toLocaleString()} buys total`}
            />
          </div>
        </header>

        <section className="adx-toolbar">
          <div className="adx-search">
            <span className="adx-search__icon" aria-hidden>⌕</span>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="search email, wallet, or tx hash…"
              spellCheck={false}
              autoComplete="off"
            />
            {query ? (
              <button
                type="button"
                className="adx-search__clear"
                onClick={() => setQuery('')}
                aria-label="Clear search"
              >×</button>
            ) : null}
          </div>
          <button
            type="button"
            className="adx-refresh"
            onClick={() => setRefreshTick((n) => n + 1)}
            disabled={loading}
          >
            {loading ? 'refreshing…' : 'refresh'}
          </button>
        </section>

        {error ? (
          <div className="adx-error">
            {error}
            <button onClick={() => setRefreshTick((n) => n + 1)}>retry</button>
          </div>
        ) : null}

        <section className="adx-table-wrap">
          <table className="adx-table">
            <thead>
              <tr>
                <th className="adx-col-num">№</th>
                <th>When</th>
                <th>Buyer</th>
                <th className="adx-num">USDT in</th>
                <th className="adx-num">{TOKEN_SYMBOL} out</th>
                <th className="adx-num">Tx</th>
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 ? (
                Array.from({ length: 6 }).map((_, i) => <SkeletonRow key={i} idx={i} />)
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="adx-empty">
                    <div className="adx-empty__title">
                      {debouncedQ ? 'No buys match that filter.' : 'Awaiting the first buy.'}
                    </div>
                    {debouncedQ ? (
                      <button className="adx-empty__clear" onClick={() => setQuery('')}>
                        clear filter
                      </button>
                    ) : null}
                  </td>
                </tr>
              ) : (
                rows.map((r, i) => {
                  const n = (page - 1) * limit + i + 1;
                  return (
                    <tr key={r.id} className="adx-row">
                      <td className="adx-col-num">{String(n).padStart(3, '0')}</td>
                      <td>
                        <div className="adx-when" title={formatAbsolute((r.boughtAt || r.createdAt))}>
                          {formatRelative((r.boughtAt || r.createdAt))}
                        </div>
                        <div className="adx-when__abs">{formatAbsolute((r.boughtAt || r.createdAt))}</div>
                      </td>
                      <td>
                        <div className="adx-buyer">
                          <span className="adx-email" title={r.email || 'no email on file'}>
                            {r.email || <span className="adx-dim">— no email —</span>}
                          </span>
                          {r.email ? <CopyButton value={r.email} label="email" /> : null}
                        </div>
                        <div className="adx-wallet">
                          <a
                            href={addressUrl('bsc', r.walletAddress)}
                            target="_blank"
                            rel="noreferrer"
                            className="adx-mono"
                          >
                            {shortAddr(r.walletAddress)}
                            <span className="adx-arrow">↗</span>
                          </a>
                          <CopyButton value={r.walletAddress} label="wallet" />
                        </div>
                      </td>
                      <td className="adx-num adx-mono">
                        {formatBase18(r.usdtIn, 2)}
                      </td>
                      <td className="adx-num adx-mono">
                        {formatBase18(r.tokensOut, 0)}
                      </td>
                      <td className="adx-num">
                        <a
                          href={txUrl('bsc', r.txHash)}
                          target="_blank"
                          rel="noreferrer"
                          className="adx-tx"
                          title={r.txHash}
                        >
                          <span className="adx-mono">{r.txHash.slice(2, 8)}</span>
                          <span className="adx-arrow">↗</span>
                        </a>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </section>

        <footer className="adx-foot">
          <div className="adx-range">
            {total === 0
              ? 'No rows'
              : <>Showing <strong>{rangeStart.toLocaleString()}–{rangeEnd.toLocaleString()}</strong> of <strong>{total.toLocaleString()}</strong></>}
          </div>
          <div className="adx-pager">
            <button onClick={() => goto(page - 1)} disabled={page <= 1 || loading}>‹ prev</button>
            <span className="adx-pager__pos">page <strong>{page}</strong> / {totalPages}</span>
            <button onClick={() => goto(page + 1)} disabled={page >= totalPages || loading}>next ›</button>
          </div>
        </footer>
      </div>
    </div>
  );
}
